import { useCallback, useSyncExternalStore } from "react";

// Per-window opt-in for exposing the AI tools to external browser agents.
// Mirrors the untethered-workspace pin: sessionStorage so it is scoped to
// this tab and dies with it, keyed by user so a sign-in switch never
// inherits another account's choice. Default off.

export const WEBMCP_BRIDGE_STORAGE_PREFIX = "sbot.webmcpBridge.";

const listeners = new Set<() => void>();
let storageListenerInstalled = false;

function storageKey(userID: string): string {
  return WEBMCP_BRIDGE_STORAGE_PREFIX + userID;
}

function defaultStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
}

export function readWebMCPBridgeEnabled(userID: string, storage: Storage | undefined = defaultStorage()): boolean {
  try {
    return storage?.getItem(storageKey(userID)) === "1";
  } catch {
    return false;
  }
}

export function writeWebMCPBridgeEnabled(
  userID: string,
  enabled: boolean,
  storage: Storage | undefined = defaultStorage(),
): void {
  try {
    if (enabled) storage?.setItem(storageKey(userID), "1");
    else storage?.removeItem(storageKey(userID));
  } catch {
    // Storage may be unavailable (private mode, quota); the toggle just won't persist.
  }
  notify();
}

export function subscribeWebMCPBridgePreference(listener: () => void): () => void {
  listeners.add(listener);
  installStorageListener();
  return () => {
    listeners.delete(listener);
  };
}

export function useWebMCPBridgePreference(
  userID: string | undefined,
): [enabled: boolean, setEnabled: (enabled: boolean) => void] {
  const read = useCallback(() => (userID ? readWebMCPBridgeEnabled(userID) : false), [userID]);
  const enabled = useSyncExternalStore(subscribeWebMCPBridgePreference, read, read);
  const setEnabled = useCallback(
    (next: boolean) => {
      if (userID) writeWebMCPBridgeEnabled(userID, next);
    },
    [userID],
  );
  return [enabled, setEnabled];
}

function notify(): void {
  for (const listener of listeners) listener();
}

function installStorageListener(): void {
  if (storageListenerInstalled || typeof window === "undefined") return;
  storageListenerInstalled = true;
  window.addEventListener("storage", (event) => {
    if (event.key === null || event.key.startsWith(WEBMCP_BRIDGE_STORAGE_PREFIX)) notify();
  });
}
